import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';

const CallbackContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const CallbackCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const RetryButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2980b9;
  }
`;

export const OAuthCallbackPage: React.FC = () => {
  const { provider } = useParams<{ provider: 'google' | 'github' }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleOAuthCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('OAuth 授权被取消或失败');
        setIsProcessing(false);
        return;
      }

      if (!code || !provider) {
        setError('无效的回调参数');
        setIsProcessing(false);
        return;
      }

      try {
        await handleOAuthCallback(provider, code);
        // 成功后会通过 AuthProvider 自动重定向到游戏页面
        navigate('/game');
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        setError(error.response?.data?.message || 'OAuth 登录失败，请重试');
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [provider, searchParams, handleOAuthCallback, navigate]);

  const handleRetry = () => {
    navigate('/auth');
  };

  return (
    <CallbackContainer>
      <CallbackCard>
        {isProcessing ? (
          <>
            <Spinner />
            <h2>正在处理登录...</h2>
            <p>请稍等，我们正在验证您的身份</p>
          </>
        ) : (
          <>
            <h2>登录失败</h2>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <RetryButton onClick={handleRetry}>
              返回登录页面
            </RetryButton>
          </>
        )}
      </CallbackCard>
    </CallbackContainer>
  );
};